<script setup lang="ts">
  import { capitalize } from 'es-toolkit';
  import { uniqueId } from 'es-toolkit/compat';
  import { computed, nextTick, ref, useAttrs, useCssModule, useSlots, watchEffect } from 'vue';

  import type { StatusColor, StatusIcon, StatusSeverity } from '../../../types/statusLevels';
  import { StatusColors, StatusIcons, StatusSeverities } from '../../../types/statusLevels';
  import { t } from '../../locale';
  import Button from '../Button/Button.vue';
  import Icon from '../Icon/Icon.vue';

  export interface DialogProps {
    /**
     * Shows or hides the dialog.
     * Usage: v-model:open="isOpen"
     */
    open?: boolean;

    /**
     * Whether the confirmation button is enabled or not.
     */
    disabled?: boolean;

    /**
     * Is this a dangerous action? Will turn the confirmation button red.
     */
    dangerZone?: boolean;

    /**
     * Header text.
     */
    header?: string;

    /**
     * Description text.
     */
    description?: string;

    /**
     * Cancel button text.
     */
    cancelText?: string;

    /**
     * Confirm button text.
     */
    confirmText?: string;

    /**
     * Callback function when the confirm button is clicked.
     */
    onConfirm?: (event: Event) => Promise<void> | void;

    /**
     * Treats it like an Alert Dialog, with only the confirmation button showing.
     *
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Window/alert}
     */
    alert?: boolean;

    /**
     * Adds a top accent border and icon next to the header.
     * This behaves similarly to the Alert component in respect to the levels, icons, and colors
     * passing `error` will treat the dialog the same as `dangerZone`
     */
    status?: StatusSeverity;
  }

  const props = withDefaults(defineProps<DialogProps>(), {
    open: false,
    disabled: false,
    dangerZone: false,
    header: t('ll.areYouSure'),
    description: '',
    cancelText: t('ll.cancel'),
    confirmText: '',
    onConfirm: () => undefined,
    alert: false,
    status: undefined,
  });

  defineOptions({
    name: 'll-dialog',
    inheritAttrs: false,
  });

  const emits = defineEmits<{
    (e: 'update:open', value: boolean): void;
    (e: 'cancel'): void;
  }>();

  const slots = useSlots();
  const attrs = useAttrs();
  const classes = useCssModule();
  const headerId = uniqueId('dialog-header-');

  const isConfirming = ref(false);
  const root = ref();

  // Set the default confirm button text to "Confirm".
  // If the `alert` prop is true, the default text is "Okay".
  // If the `confirmText` prop is provided, `confirmText` is used whether the alert prop is true.
  const modifiedConfirmText = computed(() => props.confirmText || (props.alert ? t('ll.okay') : t('ll.confirm')));

  const hasDescription = computed(() => !!slots.default || props.description);

  const statusIcon = computed<StatusIcon>(() =>
    props.status ? StatusIcons[capitalize(props.status)] : StatusIcons.Info,
  );
  const statusTitle = computed<StatusSeverity>(() =>
    props.status ? StatusSeverities[capitalize(props.status)] : StatusSeverities.Info,
  );
  const statusColor = computed<StatusColor>(() =>
    props.status ? StatusColors[capitalize(props.status)] : StatusColors.Info,
  );

  const computedDangerZone = computed(() => props.dangerZone || props.status === StatusSeverities.Error);

  function cancel() {
    emits('update:open', false);
    emits('cancel');
  }

  async function handleConfirm(event: Event) {
    if (isConfirming.value) {
      return;
    }

    isConfirming.value = true;

    try {
      await props.onConfirm(event);
    } finally {
      isConfirming.value = false;
    }
  }

  watchEffect(() => {
    if (props.open && root.value) {
      // Move focus to confirm button
      nextTick(function () {
        root.value.focus();
      });
    }
  });
</script>

<template>
  <transition name="fade">
    <dialog
      v-if="props.open"
      ref="root"
      :open="props.open"
      tabindex="0"
      :aria-labelledby="headerId"
      aria-modal="true"
      class="stash-dialog"
      :class="classes.dialog"
      :data-test="attrs['data-test'] || 'll-dialog'"
      v-bind="attrs"
      @keydown.esc="cancel"
    >
      <div
        class="stash-dialog__body"
        :class="[
          classes.body,
          `border-${statusColor}`,
          {
            'border-t-6': props.status,
          },
        ]"
        data-test="stash-dialog|body"
      >
        <div class="stash-dialog__body__content" :class="classes.content" data-test="stash-dialog|body-content">
          <div class="flex items-start" :class="hasDescription && 'mb-1.5'">
            <Icon
              v-if="props.status"
              data-test="dialog-status-icon"
              :name="statusIcon"
              :title="statusTitle"
              :class="`text-${statusColor}`"
            />
            <h3 :id="headerId" :class="{ 'ml-1.5 mt-px': props.status }">{{ props.header }}</h3>
          </div>

          <p v-if="hasDescription" class="mb-0">
            <slot v-if="slots.default"></slot>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <span v-else v-html="props.description"></span>
          </p>
        </div>

        <footer class="stash-dialog__footer" :class="classes.footer" data-test="stash-dialog|footer">
          <div class="text-right">
            <div :class="{ 'button-grid': !props.alert }">
              <Button v-if="!props.alert" data-test="button|cancel" secondary @click="cancel">
                {{ props.cancelText }}
              </Button>

              <Button
                data-test="button|confirm"
                :disabled="props.disabled"
                :is-loading="isConfirming"
                :color="computedDangerZone ? 'red' : 'blue'"
                :class="{
                  'w-full': props.alert,
                }"
                @click="handleConfirm"
              >
                {{ modifiedConfirmText }}
              </Button>
            </div>
          </div>
        </footer>
      </div>
    </dialog>
  </transition>
</template>

<style module>
  @reference "../../../styles/main.css";

  .dialog {
    background: rgb(0 0 0 / 30%);
    bottom: 0;
    display: flex;
    flex-direction: column;
    left: 0;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    place-content: center;
    position: fixed;
    right: 0;
    top: 0;
    z-index: var(--z-index-dialog);
    width: 100%;
    height: 100vh;
  }

  .body {
    @apply shadow-3xl;

    background: var(--color-white);
    color: var(--color-ice-700);
    display: flex;
    flex-direction: column;
    margin: 0 auto;
    max-height: 100vh;
    max-width: 360px;
    transition: transform 150 var(--ease-swing);
    width: 100%;

    &.grey {
      background: var(--color-ice-200);
      border-radius: var(--radius-sm);
    }

    @media (width >= theme(--breakpoint-md)) {
      border-radius: var(--radius-sm);
    }
  }

  .content {
    flex-grow: 1;
    padding: --spacing(6) var(--grid-gutter);
  }

  .footer {
    background: var(--color-ice-200);
    border-top: 1px solid var(--color-ice-500);
    display: initial; /* TEMP. Will need to fix marketplace `footer` style */
    padding: --spacing(6) var(--grid-gutter);

    @media (width >= theme(--breakpoint-md)) {
      border-bottom-left-radius: var(--radius-sm);
      border-bottom-right-radius: var(--radius-sm);
    }
  }

  :global(.fade-enter-from),
  :global(.fade-leave-active) {
    .body {
      transform: translate3d(0, --spacing(3), 0);
    }
  }
</style>
